import { useState } from 'react';
import { Box, Typography, Grid, Button, Alert } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { SectionCard } from '../../components/common';
import { ReviewQueueGrid } from '../../components/clinician';
import { DraftReviewModal, type MessageDraft, type ConversationMessage } from '../../components/clinician/DraftReviewModal';
import draftsData from '../../mocks/drafts.json';
import conversationHistory from '../../mocks/conversationHistory.json';

interface StoredDraft {
  id: string;
  patientId: string;
  content: string;
  reasoning: string;
  evidenceRefs: {
    refType: string;
    refId: string;
    label?: string;
  }[];
  status: string;
  generatedAt: string;
  patient: {
    firstName: string;
    lastName: string;
  };
}

export const ClinicianDashboardPage = () => {
  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState<MessageDraft | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [conversationMessages, setConversationMessages] = useState<ConversationMessage[]>([]);
  const [conversationOffset, setConversationOffset] = useState(0);
  const [showConversation, setShowConversation] = useState(false);
  
  // Load drafts from both mock data and localStorage (AI-generated from patient questions)
  const loadAllDrafts = (): StoredDraft[] => {
    const mockDrafts = [...draftsData] as StoredDraft[];
    const aiDrafts = JSON.parse(localStorage.getItem('aiDrafts') || '[]') as StoredDraft[];
    return [...aiDrafts, ...mockDrafts];
  };

  const allDrafts = loadAllDrafts();
  
  // Count pending drafts
  const pendingDrafts = allDrafts.filter(d => d.status === 'PENDING_REVIEW');

  // Prepare drafts for review queue
  const drafts = allDrafts.slice(0, 5).map((draft: StoredDraft) => ({
    id: draft.id,
    patientId: draft.patientId,
    patientName: `${draft.patient.firstName} ${draft.patient.lastName}`,
    draftContent: draft.content,
    rationale: draft.reasoning,
    evidenceRefs: draft.evidenceRefs.map((ref: { refType: string; refId: string; label?: string }) => ({
      refType: ref.refType as "CGM_EVENT" | "MEAL_ENTRY" | "CONTEXT_NOTE" | "LAB_RESULT" | "PROTOCOL_VERSION" | "MESSAGE_RESPONSE",
      refId: ref.refId,
      label: ref.label
    })),
    status: draft.status as "PENDING_REVIEW" | "APPROVED" | "REJECTED",
    createdAt: draft.generatedAt
  }));

  const loadConversationHistory = (patientId: string, offset: number = 0) => {
    // Get conversation history from mock data
    const patientKey = `patient-${patientId.split('-').pop()?.substring(0, 3)}` as keyof typeof conversationHistory;
    const allMessages = conversationHistory[patientKey] || [];
    
    // Load 5 messages at a time, starting from offset
    const messagesToLoad = allMessages.slice(offset, offset + 5).map(msg => ({
      ...msg,
      status: msg.status as "DRAFT" | "SENT"
    }));
    
    if (offset === 0) {
      setConversationMessages(messagesToLoad);
    } else {
      setConversationMessages(prev => [...prev, ...messagesToLoad]);
    }
    
    setConversationOffset(offset + 5);
  };

  const handleDraftClick = (draft: MessageDraft) => {
    setSelectedDraft(draft);
    setEditedContent(draft.draftContent);
    setIsEditing(false);
    setShowConversation(false);
    setConversationMessages([]);
    setConversationOffset(0);
    
    // Load initial conversation history
    loadConversationHistory(draft.patientId, 0);
    
    setOpenReviewModal(true);
  };

  const handleApprove = () => {
    console.log('Approved draft:', selectedDraft?.id);
    // Remove from localStorage if it's an AI-generated draft
    if (selectedDraft?.id.startsWith('draft-')) {
      const aiDrafts = JSON.parse(localStorage.getItem('aiDrafts') || '[]');
      const updatedDrafts = aiDrafts.filter((d: { id: string }) => d.id !== selectedDraft?.id);
      localStorage.setItem('aiDrafts', JSON.stringify(updatedDrafts));
    }
    // In real app, update draft status to APPROVED
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this draft?')) {
      console.log('Deleted draft:', selectedDraft?.id);
      // Remove from localStorage if it's an AI-generated draft
      if (selectedDraft?.id.startsWith('draft-')) {
        const aiDrafts = JSON.parse(localStorage.getItem('aiDrafts') || '[]');
        const updatedDrafts = aiDrafts.filter((d: { id: string }) => d.id !== selectedDraft?.id);
        localStorage.setItem('aiDrafts', JSON.stringify(updatedDrafts));
      }
      setOpenReviewModal(false);
    }
  };

  const handleSend = () => {
    console.log('Sending message:', editedContent);
    // Remove from localStorage if it's an AI-generated draft
    if (selectedDraft?.id.startsWith('draft-')) {
      const aiDrafts = JSON.parse(localStorage.getItem('aiDrafts') || '[]');
      const updatedDrafts = aiDrafts.filter((d: { id: string }) => d.id !== selectedDraft?.id);
      localStorage.setItem('aiDrafts', JSON.stringify(updatedDrafts));
    }
    // In real app, send the message to patient
    setOpenReviewModal(false);
  };

  const handleLoadMoreConversation = () => {
    if (selectedDraft) {
      loadConversationHistory(selectedDraft.patientId, conversationOffset);
    }
  };

  const handleViewRecord = () => {
    if (selectedDraft) {
      // Navigate to patient record
      window.open(`/nutritionist/patients/${selectedDraft.patientId}`, '_blank');
    }
  };

  const handleOrderLabs = () => {
    if (selectedDraft) {
      // Navigate to lab order page
      window.open(`/nutritionist/patients/${selectedDraft.patientId}/labs`, '_blank');
    }
  };

  const getCurrentQuestion = () => {
    // Get the most recent question from conversation history
    const recentMessage = conversationMessages.find(msg => msg.status === 'DRAFT');
    return recentMessage?.question || 'Patient inquiry from CGM data analysis';
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Nutritionist Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Review AI-generated drafts and monitor patient progress
      </Typography>

      {/* Pending Reviews Alert */}
      {pendingDrafts.length > 0 && (
        <Alert
          severity="warning"
          icon={<RateReviewIcon />}
          sx={{ mb: 3 }}
          action={
            <Button
              component={RouterLink}
              to="/nutritionist/drafts"
              color="inherit"
              size="small"
            >
              Review Now
            </Button>
          }
        >
          {pendingDrafts.length} AI-generated message{pendingDrafts.length > 1 ? 's' : ''} waiting for your review
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Recent Draft Queue */}
        <Grid item xs={12}>
          <SectionCard
            title="Recent AI Drafts"
            subtitle="Requires human-in-the-loop approval"
            actions={
              <Button
                component={RouterLink}
                to="/nutritionist/drafts"
                size="small"
              >
                View All
              </Button>
            }
          >
            <ReviewQueueGrid
              drafts={drafts}
              onDraftClick={handleDraftClick}
            />
          </SectionCard>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12}>
          <SectionCard title="Quick Actions">
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                component={RouterLink}
                to="/nutritionist/drafts"
              >
                Review Drafts
              </Button>
              <Button
                variant="outlined"
                component={RouterLink}
                to="/nutritionist/patients"
              >
                Patient List
              </Button>
              <Button
                variant="outlined"
                component={RouterLink}
                to="/nutritionist/gaps"
              >
                Knowledge Gaps
              </Button>
              <Button
                variant="outlined"
                component={RouterLink}
                to="/nutritionist/cgm-import"
              >
                Import CGM Data
              </Button>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Draft Review Modal */}
      <DraftReviewModal
        open={openReviewModal}
        selectedDraft={selectedDraft}
        editedContent={editedContent}
        setEditedContent={setEditedContent}
        isEditing={isEditing}
        showConversation={showConversation}
        setShowConversation={setShowConversation}
        conversationMessages={conversationMessages}
        currentQuestion={getCurrentQuestion()}
        onClose={() => setOpenReviewModal(false)}
        onApprove={handleApprove}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onSend={handleSend}
        onLoadMoreConversation={handleLoadMoreConversation}
        onViewRecord={handleViewRecord}
        onOrderLabs={handleOrderLabs}
      />
    </Box>
  );
};
